import { createServerFn } from "@tanstack/react-start";
import { setResponseHeaders } from "@tanstack/react-start/server";

const STORE_ID = "e435da0b-10d0-46fa-a389-702295c89840";
const SB_URL = "https://bdvjinttdlvhwcnhfycc.supabase.co";
const SB_ANON =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImJkdmppbnR0ZGx2aHdjbmhmeWNjIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjcwMTQ0ODAsImV4cCI6MjA4MjU5MDQ4MH0.rSnXEYrhlhfh9AcQrrzdn0FVh9TpmcpLPmYk9GR-pj8";

// Category mapping (verified from Fyndanu categories table)
export const CAT = {
  HUSBIL: "0c33dd2e-dc3c-497c-9588-d96cb629378c",
  HUSVAGN: "7a4f8acd-bae9-4725-8dcc-11e57f294204",
  BIL: "42e3060f-144d-49cf-ae72-3c13ded79c1d",
  SLAPVAGN: "e546ebab-dc64-4bc6-9449-8e09825e3554",
} as const;

export type VehicleCategory = "husbil" | "husvagn" | "bil" | "slapvagn" | "annat";

export type FyndanuListing = {
  id: string;
  title: string;
  price: number | null;
  image: string;
  images: string[];
  location: string | null;
  url: string;
  category: VehicleCategory;
  // parsed spec fields (best-effort from description)
  brand: string | null;
  model: string | null;
  year: number | null;
  mileage: number | null; // mil (Swedish, 1 mil = 10 km)
  fuel: string | null;
  transmission: string | null;
  power: string | null; // "146 hk"
  bodyType: string | null;
  length: number | null; // meters
  totalWeight: number | null; // kg
  serviceWeight: number | null; // kg
  sleepingSpots: number | null;
  seatBelts: number | null;
  regNo: string | null;
  description: string;
  createdAt: string;
  isFeatured: boolean;
  equipment: string[];
};

function categoryOf(id: string | null): VehicleCategory {
  if (id === CAT.HUSBIL) return "husbil";
  if (id === CAT.HUSVAGN) return "husvagn";
  if (id === CAT.BIL) return "bil";
  if (id === CAT.SLAPVAGN) return "slapvagn";
  return "annat";
}

function sharpen(u: string, w = 900) {
  const transformed = u.replace(
    "/storage/v1/object/public/",
    "/storage/v1/render/image/public/",
  );
  const sep = transformed.includes("?") ? "&" : "?";
  return `${transformed}${sep}width=${w}&quality=85&resize=cover`;
}

/** Parse the "Tekniska specifikationer" bullet block Fyndanu writes. */
function parseSpecs(description: string): Partial<FyndanuListing> {
  const s = description || "";
  const grab = (label: string): string | null => {
    const re = new RegExp(`[•\\-]\\s*${label}\\s*:\\s*([^\\n]+)`, "i");
    const m = s.match(re);
    return m ? m[1].trim() : null;
  };
  const num = (v: string | null): number | null => {
    if (!v) return null;
    const n = parseFloat(v.replace(/\s/g, "").replace(",", "."));
    return Number.isFinite(n) ? n : null;
  };
  const int = (v: string | null): number | null => {
    if (!v) return null;
    const n = parseInt(v.replace(/\D/g, ""), 10);
    return Number.isFinite(n) ? n : null;
  };

  // Equipment list: everything after "Utrustning:" up to "Kontakta"
  const equipment: string[] = [];
  const eqMatch = s.match(/Utrustning:\s*\n([\s\S]*?)(?=\n\nKontakta|\n\n[A-ZÅÄÖ]|$)/);
  if (eqMatch) {
    for (const line of eqMatch[1].split("\n")) {
      const m = line.match(/^[•\-]\s*(.+?)\s*$/);
      if (m && m[1].length < 80) equipment.push(m[1]);
    }
  }

  return {
    brand: grab("Märke"),
    model: grab("Modell"),
    year: int(grab("Modellår")),
    mileage: int(grab("Miltal")),
    fuel: grab("Bränsle"),
    transmission: grab("Växellåda"),
    power: grab("Effekt"),
    bodyType: grab("Kaross"),
    length: num(grab("Längd")?.replace(/\s*m\s*$/i, "") ?? null),
    totalWeight: int(grab("Totalvikt")),
    serviceWeight: int(grab("Tjänstevikt")),
    sleepingSpots: int(grab("Sovplatser")),
    seatBelts: int(grab("Sittplatser med bälte")),
    regNo: grab("Reg\\.nr"),
    equipment: equipment.slice(0, 40),
  };
}

type RawRow = {
  id: string;
  title: string;
  price: number | null;
  images: string[] | null;
  location: string | null;
  category_id: string | null;
  description: string | null;
  created_at: string;
  is_featured: boolean | null;
};

function mapRow(r: RawRow): FyndanuListing | null {
  if (!Array.isArray(r.images) || r.images.length === 0) return null;
  const specs = parseSpecs(r.description ?? "");
  const imgs = r.images.map((u) => sharpen(u, 1400));
  return {
    id: r.id,
    title: r.title,
    price: r.price,
    image: sharpen(r.images[0]),
    images: imgs,
    location: r.location,
    url: `https://www.fyndanu.se/listing/${r.id}`,
    category: categoryOf(r.category_id),
    description: r.description ?? "",
    createdAt: r.created_at,
    isFeatured: !!r.is_featured,
    brand: null,
    model: null,
    year: null,
    mileage: null,
    fuel: null,
    transmission: null,
    power: null,
    bodyType: null,
    length: null,
    totalWeight: null,
    serviceWeight: null,
    sleepingSpots: null,
    seatBelts: null,
    regNo: null,
    equipment: [],
    ...specs,
  };
}

const SELECT_COLS =
  "id,title,price,images,location,category_id,description,created_at,is_featured";

export const getFyndanuListings = createServerFn({ method: "GET" }).handler(
  async (): Promise<{ items: FyndanuListing[]; error: string | null }> => {
    try {
      const url =
        `${SB_URL}/rest/v1/listings?user_id=eq.${STORE_ID}` +
        `&status=eq.active&select=${SELECT_COLS}` +
        `&order=is_featured.desc,created_at.desc&limit=100`;

      const res = await fetch(url, {
        headers: { apikey: SB_ANON, Authorization: `Bearer ${SB_ANON}` },
      });

      if (!res.ok) return { items: [], error: `Fyndanu API ${res.status}` };

      const raw = (await res.json()) as RawRow[];
      const items = raw.map(mapRow).filter((x): x is FyndanuListing => x !== null);

      setResponseHeaders(
        new Headers({
          "Cache-Control":
            "public, max-age=300, s-maxage=300, stale-while-revalidate=3600",
        }),
      );
      return { items, error: null };
    } catch (err) {
      console.error("getFyndanuListings failed", err);
      return { items: [], error: "Kunde inte hämta fordon just nu" };
    }
  },
);

export const getFyndanuListing = createServerFn({ method: "GET" })
  .inputValidator((data: { id: string }) => {
    if (!data?.id || typeof data.id !== "string") throw new Error("id required");
    return data;
  })
  .handler(async ({ data }): Promise<{ item: FyndanuListing | null; error: string | null }> => {
    try {
      const url =
        `${SB_URL}/rest/v1/listings?id=eq.${data.id}` +
        `&user_id=eq.${STORE_ID}&select=${SELECT_COLS}&limit=1`;
      const res = await fetch(url, {
        headers: { apikey: SB_ANON, Authorization: `Bearer ${SB_ANON}` },
      });
      if (!res.ok) return { item: null, error: `Fyndanu API ${res.status}` };
      const raw = (await res.json()) as RawRow[];
      const item = raw[0] ? mapRow(raw[0]) : null;
      setResponseHeaders(
        new Headers({
          "Cache-Control":
            "public, max-age=300, s-maxage=300, stale-while-revalidate=3600",
        }),
      );
      return { item, error: null };
    } catch (err) {
      console.error("getFyndanuListing failed", err);
      return { item: null, error: "Kunde inte hämta fordon just nu" };
    }
  });
